import { apiService } from './api';

/**
 * Server-stored per-user UI preferences (completed tours, dismissed checklist)
 * and the computed first-run setup checklist. Preferences live server-side
 * because POS terminals are shared machines — state must follow the login.
 */

export interface OnboardingStatus {
  companyProfile: boolean;
  hasProducts: boolean;
  hasTaxes: boolean;
  printerConfigured: boolean;
  hasFirstSale: boolean;
}

export const uxService = {
  getPreferences: () =>
    apiService.get<Record<string, string>>('/user-preferences'),

  setPreference: (key: string, value: string) =>
    apiService.put<void>(`/user-preferences/${encodeURIComponent(key)}`, { value }),

  getOnboardingStatus: () =>
    apiService.get<OnboardingStatus>('/onboarding/status'),
};

// Simple in-memory cache so every page mount doesn't refetch preferences.
let prefsCache: Record<string, string> | null = null;
let prefsPromise: Promise<Record<string, string>> | null = null;

export const getCachedPreferences = (): Promise<Record<string, string>> => {
  if (prefsCache) return Promise.resolve(prefsCache);
  if (!prefsPromise) {
    prefsPromise = uxService.getPreferences()
      .then((res) => {
        prefsCache = res.data.data ?? {};
        return prefsCache;
      })
      .catch(() => {
        prefsPromise = null; // allow retry on next call
        return {};
      });
  }
  return prefsPromise;
};

export const setCachedPreference = (key: string, value: string): Promise<void> => {
  if (prefsCache) prefsCache[key] = value;
  return uxService.setPreference(key, value).then(() => undefined).catch(() => undefined);
};

/** Reset the cache on logout so the next user's preferences are fetched fresh. */
export const clearPreferenceCache = () => {
  prefsCache = null;
  prefsPromise = null;
};
